/**
 * Карточка контакта — кому сейчас звоним.
 *
 * Показывает то и только то, что нужно перед звонком: номер, круг, источник,
 * сколько попыток уже сделано. Кнопка набора живёт в центральной панели звонка,
 * а не здесь: действие на пульте одно, и оно всегда в одном месте.
 *
 * Карточка приходит в снапшоте пульта, тип берётся из контракта (@cc/shared,
 * deskAssignmentSchema): своё описание того же ответа разъедется с сервером молча.
 */

import type { DeskAssignment } from '@cc/shared';

import { formatTzOffset } from '@cc/shared';

import { carrierBrand } from '../../shared/lib/carrier.js';
import { formatPhone } from '../../shared/lib/format-phone.js';
import { localTimeOf } from '../../shared/lib/local-time.js';
import { formatDateTimeMsk } from '../../shared/lib/msk.js';
import { formatDuration } from '../../shared/lib/format-duration.js';
import { cardDueState } from './callback-alarm.js';
import { useNow } from './use-now.js';

export interface ContactCardProps {
  assignment: DeskAssignment;
}

export function ContactCard({ assignment }: ContactCardProps): React.JSX.Element {
  // «Пора»/«просрочен» на плашке договорённости наступают сами, по часам
  const now = useNow(30_000);
  const due = cardDueState(assignment.lastResult?.nextActionAt, now);
  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5">
      <header className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold tracking-wide text-slate-500 uppercase">
          Карточка контакта
        </h2>
        <p className="text-2xl font-extrabold tabular-nums tracking-tight text-slate-900">
          {formatPhone(assignment.phone)}
        </p>
        <p className="min-w-0 text-base break-words text-slate-600">
          {assignment.fullName ?? 'Имя не передано'}
          {assignment.regionCode !== null ? `, ${assignment.regionCode}` : ''}
        </p>
        {/* Который час у человека на том конце. «Звоню в обед» и «звоню
            в семь утра» — разные разговоры, а диспетчер видит только свои
            часы и по умолчанию считает, что у абонента столько же.
            Оператор — из реестра диапазонов: «что за телефон» видно до гудков */}
        <p className="text-sm text-slate-500">
            {assignment.carrier !== null ? (
              <span className="font-semibold text-slate-700">
                {carrierBrand(assignment.carrier)}
              </span>
            ) : null}
            {assignment.carrier !== null ? ' · ' : ''}
            {assignment.tzOffsetMin !== null ? (
              <>
                у абонента сейчас{' '}
                <span className="font-semibold text-slate-700">
                  {localTimeOf(assignment.tzOffsetMin)}
                </span>{' '}
                · {formatTzOffset(assignment.tzOffsetMin)}
              </>
            ) : (
              // Реестр пояса не знает (часть диапазонов Tele2 без географии):
              // окно звонка считает такие номера московскими — так и говорим,
              // пустота читалась бы как «время неважно»
              <>
                пояс неизвестен — считаем по Москве, сейчас{' '}
                <span className="font-semibold text-slate-700">{localTimeOf(180)}</span>
              </>
            )}
        </p>
      </header>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-slate-100 pt-4 text-sm">
        <div>
          <dt className="text-slate-500">Круг</dt>
          <dd className="font-semibold text-slate-900">
            {assignment.roundNumber}
            {assignment.roundNumber > 1 ? ' (НДЗ)' : ''}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Источник</dt>
          <dd className="min-w-0 font-semibold break-words text-slate-900">{assignment.sourceTitle}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Выдан</dt>
          <dd className="font-semibold text-slate-900">
            {formatDateTimeMsk(assignment.assignedAt)}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Попыток</dt>
          <dd className="font-semibold text-slate-900">
            {assignment.callsCnt}
            {assignment.callsCnt > 0
              ? ` · в разговоре ${formatDuration(assignment.talkSec)}`
              : ''}
          </dd>
        </div>
      </dl>

      {assignment.callsCnt > 0 ? (
        <p className="text-sm text-slate-500">
          {assignment.isReached ? 'Разговор уже был.' : 'Дозвона пока не было.'}
        </p>
      ) : null}

      {/* Прошлое закрытие этой же карточки: перезвон и НДЗ без «о чём
          договорились в прошлый раз» заставляют звонить вслепую.
          Truthy-проверка, а не !== null: в окне деплоя «новый web + старый api»
          поле приходит undefined, и строгое сравнение уронило бы весь пульт */}
      {assignment.lastResult ? (
        <div
          className={
            'flex flex-col gap-1 rounded-xl px-3 py-2.5 text-sm ' +
            (due === 'overdue'
              ? 'bg-red-50 text-red-900'
              : due === 'due'
                ? 'bg-amber-50 text-amber-900'
                : assignment.lastResult.nextActionAt !== null
                  ? 'bg-blue-50 text-blue-900'
                  : 'bg-slate-50 text-slate-700')
          }
        >
          {/* Время договорённости наступило — карточка говорит об этом сама,
              а не только строка в блоке «Перезвоны», которая после выдачи
              исчезает (06.09.2026) */}
          {due === 'due' ? (
            <p className="font-bold">Пора перезвонить</p>
          ) : due === 'overdue' ? (
            <p className="font-bold">Перезвон просрочен</p>
          ) : null}
          <p>
            <span className="font-semibold">{assignment.lastResult.labelTitle}</span>
            {' · '}
            {formatDateTimeMsk(assignment.lastResult.labeledAt)}
            {assignment.lastResult.nextActionAt !== null
              ? ` — договорились на ${formatDateTimeMsk(assignment.lastResult.nextActionAt)}`
              : ''}
          </p>
          {assignment.lastResult.comment !== null ? (
            <p className="whitespace-pre-line">{assignment.lastResult.comment}</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

